'use client'

import { usePlausible } from 'next-plausible'
import { useUser } from '@auth0/nextjs-auth0'
import { useCallback, useRef } from 'react'

import type { TVesselJourneyResponse } from '@/api/v1/resources/vesselJourney'
import { STORAGE_KEY } from '@/app/constants'
import { useLocalStorage } from '@/app/hooks/useLocalStorage'

// Event names matching your Plausible configuration
export const ANALYTICS_EVENTS = {
  // Engagement Events
  FLEET_ADD: 'fleet.add',
  FLEET_REMOVE: 'fleet.remove',
  NOTIFICATION_SET: 'notification.set',
  NOTIFICATION_OTHERS_SET: 'notification.others.set',
  NOTIFICATION_CTA: 'notification.cta',
  // Sharing Events
  VESSEL_SHARED: 'vessel.shared',
  VESSEL_SHARED_CTA: 'vessel.shared.cta',
  VESSEL_SEARCHED: 'vessel.searched',
  // Retention Events
  ACTIVATION_COMPLETED: 'activation.completed',
  // Content Usage Events
  VESSEL_VIEWED: 'vessel.viewed',
  TOOLTIP_LEARN_MORE: 'tooltip.learnmore',
  // Registration Events
  REGISTER_CTA: 'register.cta',
  // Lineup Events
  LINEUP_CREATED: 'lineup.created',
  LINEUP_SHARED: 'lineup.shared',
  LINEUP_SHARED_EMAIL_CTA: 'lineup.shared.email.cta',
  LINEUP_VESSEL_ADDED: 'lineup.vessel.added',
  LINEUP_CTA: 'lineup.cta',
  LINEUP_VIEWED: 'lineup.viewed',
  // Community Update Events
  COMMUNITY_UPDATE_CREATED: 'community.update.created',
  COMMUNITY_UPDATE_CTA: 'community.update.cta',
  COMMUNITY_UPDATE_VIEWED: 'community.update.viewed',
  // Onboard Questionnaire Events
  ON_BOARD_QUESTIONNAIRE_OPENED: 'onboard.questionnaire.opened',
  ON_BOARD_QUESTIONNAIRE_SKIPPED: 'onboard.questionnaire.skipped',
  ON_BOARD_QUESTIONNAIRE_COMPLETED: 'onboard.questionnaire.completed',
  ON_BOARD_QUESTIONNAIRE_ROLE: 'onboard.questionnaire.role',
  ON_BOARD_QUESTIONNAIRE_LOOK_FOR_INFORMATION: 'onboard.questionnaire.lookForInformation',
  // Onboarding Tour Events
  ONBOARDING_TOUR_STARTED: 'onboard.tour.started',
  ONBOARDING_TOUR_COMPLETED: 'onboard.tour.completed',
  ONBOARDING_TOUR_SKIPPED: 'onboard.tour.skipped',
  // Redirect Events
  TEQPLAY_REDIRECT_CTA: 'teqplay.redirect.cta'
} as const

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS]

// Source types
export type TSource = 'homepage' | 'bookmark' | 'share_link' | 'direct'

// Event properties interface
export type TAnalyticsEventProps = {
  imo?: string
  vesselName?: string
  portId?: string
  portName?: string
  // Fleet specific
  fleetSize?: number
  // Notification specific
  eventType?: string
  medium?: 'EMAIL' | 'WHATSAPP'
  timing?: string
  // Sharing specific
  shareMethod?: 'EMAIL' | 'WHATSAPP'
  recipientCount?: number
  // Content specific
  source?: TSource | string
  // Activation specific
  actionType?: AnalyticsEvent
  // Lineup specific
  lineupId?: string
  lineupName?: string
  vesselCount?: number
  // Community update specific
  contributionType?: 'note' | 'structured'
  // Onboard questionnaire specific
  role?: string
  lookForInformation?: string
  // Onboarding tour specific
  stepIndex?: number

  // Redirect specific
  url?: string

  // User context
  userType?: 'authenticated' | 'anonymous'
}

/**
 * Helper function to create analytics-compatible vessel object
 * Ensures vessel name is always a string for analytics tracking
 */
export const createAnalyticsVessel = (vessel: TVesselJourneyResponse) => ({
  ship: {
    imo: vessel.ship.imo,
    name: vessel.ship.name || 'Unknown Vessel'
  }
})

/**
 * Generate a unique event ID for deduplication
 * Creates a stable ID based on event name and properties (without timestamp)
 */
const generateEventId = (eventName: string, props?: TAnalyticsEventProps): string => {
  const propsString = props ? JSON.stringify(props) : ''
  return `${eventName}-${propsString}`
}

/**
 * Custom hook for tracking analytics events with Plausible
 */
export function useAnalytics() {
  const plausible = usePlausible()
  const { user, isLoading: isUserLoading } = useUser()

  // Track recent events to prevent duplicates within a short time window
  const recentEvents = useRef<Set<string>>(new Set())

  // Use localStorage hook to track if user is old (has performed actions before)
  const [isOldUser, setIsOldUser] = useLocalStorage<boolean>(STORAGE_KEY.IS_OLD_USER, false)

  const track = useCallback(
    (eventName: AnalyticsEvent, props?: TAnalyticsEventProps, allowDuplicates = false) => {
      try {
        // Filter out undefined values to keep the payload clean
        const cleanProps = props
          ? Object.fromEntries(Object.entries(props).filter(([, value]) => value !== undefined))
          : undefined

        // Generate event ID for deduplication (excluding timestamp-sensitive events)
        if (!allowDuplicates) {
          const eventId = generateEventId(eventName, cleanProps)

          // Check if we've already tracked this exact event recently
          if (recentEvents.current.has(eventId)) {
            return
          }
          // Add to recent events and clean up old entries after 5 seconds
          recentEvents.current.add(eventId)
          setTimeout(() => {
            recentEvents.current.delete(eventId)
          }, 5000)
        }
        plausible(eventName, { props: cleanProps })

        // Mark user as old and send activation event if this is their first action
        if (!isOldUser && user && eventName !== ANALYTICS_EVENTS.VESSEL_VIEWED) {
          setIsOldUser(true)
          plausible(ANALYTICS_EVENTS.ACTIVATION_COMPLETED, {
            props: {
              ...cleanProps,
              actionType: eventName,
              userType: 'authenticated'
            }
          })
        }
      } catch (error) {
        console.error('Analytics tracking error:', error)
      }
    },
    [plausible, user, isOldUser, setIsOldUser]
  )

  // Convenience methods for common events
  const trackFleetAdd = (vessel: { ship: { imo: string; name: string } }, fleetSize?: number) => {
    track(ANALYTICS_EVENTS.FLEET_ADD, {
      imo: vessel.ship.imo,
      vesselName: vessel.ship.name,
      fleetSize,
      userType: user ? 'authenticated' : 'anonymous'
    })
  }

  const trackFleetRemove = (
    vessel: { ship: { imo: string; name: string } },
    fleetSize?: number
  ) => {
    track(ANALYTICS_EVENTS.FLEET_REMOVE, {
      imo: vessel.ship.imo,
      vesselName: vessel.ship.name,
      fleetSize,
      userType: user ? 'authenticated' : 'anonymous'
    })
  }

  const trackNotificationSet = (props: {
    imo: string
    vesselName: string
    eventType: string
    medium: 'EMAIL' | 'WHATSAPP'
    timing?: string
    portName?: string
  }) => {
    track(ANALYTICS_EVENTS.NOTIFICATION_SET, {
      ...props,
      userType: 'authenticated'
    })
  }

  const trackNotificationOthersSet = (props: {
    imo: string
    vesselName: string
    eventType: string
    medium: 'EMAIL' | 'WHATSAPP'
    timing?: string
    portName?: string
  }) => {
    track(ANALYTICS_EVENTS.NOTIFICATION_OTHERS_SET, {
      ...props,
      userType: 'authenticated'
    })
  }

  const trackNotificationCTA = () => {
    track(ANALYTICS_EVENTS.NOTIFICATION_CTA)
  }

  const trackVesselShared = (props: {
    imo: string
    vesselName: string
    shareMethod: 'EMAIL' | 'WHATSAPP'
    recipientCount: number
    portName?: string
  }) => {
    track(ANALYTICS_EVENTS.VESSEL_SHARED, {
      ...props,
      userType: 'authenticated'
    })
  }

  const trackVesselSharedCTA = () => {
    track(ANALYTICS_EVENTS.VESSEL_SHARED_CTA)
  }

  const trackVesselViewed = (props: {
    imo: string
    vesselName: string
    source: TSource
    userType: 'authenticated' | 'anonymous'
    portName?: string
  }) => {
    track(ANALYTICS_EVENTS.VESSEL_VIEWED, props)
  }

  const trackVesselSearched = (props: { imo: string; vesselName: string }) => {
    track(ANALYTICS_EVENTS.VESSEL_SEARCHED, {
      ...props,
      userType: user ? 'authenticated' : 'anonymous'
    })
  }

  const trackRegisterCTA = () => {
    track(ANALYTICS_EVENTS.REGISTER_CTA)
  }

  const trackTooltipLearnMore = (props?: { source?: string; tooltipType?: string }) => {
    track(ANALYTICS_EVENTS.TOOLTIP_LEARN_MORE, {
      ...props,
      userType: user ? 'authenticated' : 'anonymous'
    })
  }

  // Lineup tracking methods
  const trackLineupCreated = (props: { lineupId: string; lineupName: string }) => {
    track(ANALYTICS_EVENTS.LINEUP_CREATED, {
      ...props,
      userType: 'authenticated'
    })
  }

  const trackLineupShared = (props: {
    lineupId: string
    lineupName: string
    shareMethod: 'EMAIL' | 'WHATSAPP'
    recipientCount: number
  }) => {
    track(ANALYTICS_EVENTS.LINEUP_SHARED, {
      ...props,
      userType: 'authenticated'
    })
  }

  const trackLineupVesselAdded = (props: {
    lineupId: string
    lineupName: string
    imo: string
    vesselName: string
    vesselCount: number
  }) => {
    track(ANALYTICS_EVENTS.LINEUP_VESSEL_ADDED, {
      ...props,
      userType: 'authenticated'
    })
  }

  const trackLineupCTA = () => {
    track(ANALYTICS_EVENTS.LINEUP_CTA, {
      userType: 'anonymous'
    })
  }

  const trackLineupViewed = () => {
    track(ANALYTICS_EVENTS.LINEUP_VIEWED, {
      userType: 'authenticated'
    })
  }

  const trackLineupSharedEmailCTA = () => {
    track(ANALYTICS_EVENTS.LINEUP_SHARED_EMAIL_CTA, {
      userType: user ? 'authenticated' : 'anonymous'
    })
  }

  // Community update tracking methods
  const trackCommunityUpdateCreated = (props: {
    imo: string
    vesselName: string
    portName?: string
    contributionType: 'note' | 'structured'
  }) => {
    track(ANALYTICS_EVENTS.COMMUNITY_UPDATE_CREATED, {
      ...props,
      userType: 'authenticated'
    })
  }

  const trackCommunityUpdateCTA = (props: { imo: string; vesselName: string }) => {
    track(ANALYTICS_EVENTS.COMMUNITY_UPDATE_CTA, {
      ...props,
      userType: 'anonymous'
    })
  }

  const trackCommunityUpdateViewed = (props: { imo: string; vesselName: string }) => {
    track(ANALYTICS_EVENTS.COMMUNITY_UPDATE_VIEWED, {
      ...props,
      userType: 'authenticated'
    })
  }

  // Onboard questionnaire tracking methods
  const trackOnBoardQuestionnaireOpened = useCallback(() => {
    track(ANALYTICS_EVENTS.ON_BOARD_QUESTIONNAIRE_OPENED, {
      userType: user ? 'authenticated' : 'anonymous'
    })
  }, [track, user])

  const trackOnBoardQuestionnaireSkipped = useCallback(() => {
    track(ANALYTICS_EVENTS.ON_BOARD_QUESTIONNAIRE_SKIPPED, {
      userType: user ? 'authenticated' : 'anonymous'
    })
  }, [track, user])

  const trackOnBoardQuestionnaireCompleted = useCallback(() => {
    track(ANALYTICS_EVENTS.ON_BOARD_QUESTIONNAIRE_COMPLETED, {
      userType: user ? 'authenticated' : 'anonymous'
    })
  }, [track, user])

  const trackOnBoardQuestionnaireRole = useCallback(
    (role: string) => {
      track(ANALYTICS_EVENTS.ON_BOARD_QUESTIONNAIRE_ROLE, {
        role,
        userType: user ? 'authenticated' : 'anonymous'
      })
    },
    [track, user]
  )

  const trackOnBoardQuestionnaireLookForInformation = useCallback(
    (lookForInformation: string) => {
      track(ANALYTICS_EVENTS.ON_BOARD_QUESTIONNAIRE_LOOK_FOR_INFORMATION, {
        lookForInformation,
        userType: user ? 'authenticated' : 'anonymous'
      })
    },
    [track, user]
  )

  // Onboarding tour tracking methods
  const trackOnboardingTourStarted = useCallback(
    (tourType: 'MAIN' | 'VESSEL_DETAILS') => {
      track(ANALYTICS_EVENTS.ONBOARDING_TOUR_STARTED, {
        source: tourType,
        userType: user ? 'authenticated' : 'anonymous'
      })
    },
    [track, user]
  )

  const trackOnboardingTourCompleted = useCallback(
    (tourType: 'MAIN' | 'VESSEL_DETAILS') => {
      track(ANALYTICS_EVENTS.ONBOARDING_TOUR_COMPLETED, {
        source: tourType,
        userType: user ? 'authenticated' : 'anonymous'
      })
    },
    [track, user]
  )

  const trackOnboardingTourSkipped = useCallback(
    (tourType: 'MAIN' | 'VESSEL_DETAILS', stepIndex: number) => {
      track(ANALYTICS_EVENTS.ONBOARDING_TOUR_SKIPPED, {
        source: tourType,
        stepIndex,
        userType: user ? 'authenticated' : 'anonymous'
      })
    },
    [track, user]
  )

  const trackTeqplayRedirectCTA = useCallback(
    (props: { source: string; url: string }) => {
      track(ANALYTICS_EVENTS.TEQPLAY_REDIRECT_CTA, {
        ...props,
        userType: user ? 'authenticated' : 'anonymous'
      })
    },
    [track, user]
  )

  return {
    // Ready state - analytics is ready when user loading is complete
    isAnalyticsReady: !isUserLoading,

    track,
    trackFleetAdd,
    trackFleetRemove,
    trackNotificationSet,
    trackNotificationOthersSet,
    trackNotificationCTA,
    trackVesselShared,
    trackVesselSharedCTA,
    trackVesselViewed,
    trackVesselSearched,
    trackRegisterCTA,
    trackTooltipLearnMore,
    trackLineupCreated,
    trackLineupShared,
    trackLineupVesselAdded,
    trackLineupCTA,
    trackLineupViewed,
    trackLineupSharedEmailCTA,
    trackCommunityUpdateCreated,
    trackCommunityUpdateCTA,
    trackCommunityUpdateViewed,
    trackOnBoardQuestionnaireOpened,
    trackOnBoardQuestionnaireSkipped,
    trackOnBoardQuestionnaireCompleted,
    trackOnBoardQuestionnaireRole,
    trackOnBoardQuestionnaireLookForInformation,
    trackOnboardingTourStarted,
    trackOnboardingTourCompleted,
    trackOnboardingTourSkipped,
    trackTeqplayRedirectCTA
  }
}
